import type {QueryClient} from '@tanstack/react-query'
import {storageUsageQueryKey} from '@/shared/utils/hooks/useAPIFiles'
import {fileQueryKeys, filesListQueryKey} from '../core/storage'

/**
 * File-listing query keys for a changed folder AND its ancestors, in the exact
 * `${identityId}/${relativePath}` form FileTable uses (see filePathForQuery). Invalidating the
 * whole lineage means whichever folder the user is currently viewing refetches — the changed
 * folder itself, or an ancestor whose listing now shows a new subfolder. Siblings are untouched.
 * At most one of these queries is active, so this triggers at most one immediate refetch.
 */
export const buildFolderLineageKeys = (identityId: string, relativeFolder: string): string[] => {
  const segments = relativeFolder.split('/').filter(Boolean)
  const keys: string[] = [`${identityId}/`] // root (matches filePathForQuery at root)
  let acc = ''
  for (const segment of segments) {
    acc = acc ? `${acc}/${segment}` : segment
    keys.push(`${identityId}/${acc}`)
  }
  return keys
}

/**
 * Targeted Files refresh for changed folders (root-relative): each folder's lineage is invalidated
 * once, leaving unrelated folders cached. Without an identity yet, falls back to a coarse refresh
 * so the change is not missed.
 */
export function invalidateChangedFolders(
  queryClient: QueryClient,
  identityId: string | undefined,
  relativeFolders: readonly string[]
): void {
  if (identityId) {
    const keys = new Set(relativeFolders.flatMap((f) => buildFolderLineageKeys(identityId, f)))
    keys.forEach((filePath) => queryClient.invalidateQueries(filesListQueryKey(filePath)))
  } else {
    queryClient.invalidateQueries(fileQueryKeys.all)
  }
  queryClient.invalidateQueries(storageUsageQueryKey)
}

/**
 * Targeted refresh for a change inside a shared folder (`files.changed` with a `shareId`). `path` is
 * relative to the shared folder, as the notification sends it: `''` for the shared folder itself,
 * `a/` or `a/b/` below it. The changed folder and its ancestors up to the shared folder are
 * invalidated, and nothing in the user's own space: that path is not in their space at all.
 */
export function invalidateSharedFolder(
  queryClient: QueryClient,
  shareId: string,
  path: string
): void {
  const segments = path.split('/').filter(Boolean)
  for (let depth = 0; depth <= segments.length; depth++) {
    queryClient.invalidateQueries(
      fileQueryKeys.shareFolder(shareId, segments.slice(0, depth).join('/'))
    )
  }
}
